import {
  HistoryWindow,
  ServiceState,
  StatusHistoryDay,
  StatusHistoryGroupSeries,
} from '../../core/domain/status.model';

/**
 * Same order as the API's ServiceStatusAggregation. Unknown is not a severity: it means "no signal"
 * and must never win over a state that was measured.
 */
export const SEVERITY: Record<ServiceState, number> = {
  Unknown: -1,
  Operational: 0,
  Maintenance: 1,
  Degraded: 2,
  PartialOutage: 3,
  MajorOutage: 4,
};

/** Cells the history bar draws at most: the API serves 90 days. */
export const HISTORY_DAYS = 90;

/**
 * How many history cells the bar may draw at a given viewport width. 90 cells only fit
 * on a roomy screen. On a phone card (~290px of usable width) they force a horizontal
 * overflow that scrolls the whole page sideways, so narrow viewports get a shorter,
 * still-legible window instead. Ordered widest-first; the first match wins.
 */
const DAY_WINDOWS: ReadonlyArray<{ minWidth: number; days: number }> = [
  { minWidth: 900, days: 90 },
  { minWidth: 700, days: 60 },
  { minWidth: 500, days: 45 },
  { minWidth: 0, days: 30 },
];

/**
 * A bar may legitimately open with a gap, e.g. a single day the poller missed. Only a longer
 * leading run of empty cells means the series itself starts inside the window, which is
 * what the "monitored since" hint is for.
 */
const LEADING_GAP_TOLERANCE = 3;

/** Widest window whose breakpoint the viewport still clears. */
export function resolveDayCount(width: number): number {
  return (DAY_WINDOWS.find(w => width >= w.minWidth) ?? DAY_WINDOWS[DAY_WINDOWS.length - 1]).days;
}

/** 90 cells, oldest first, ending today (UTC). A day without data is null. */
export function toCells(days: StatusHistoryDay[], today: Date = new Date()): Array<StatusHistoryDay | null> {
  const byDate = new Map(days.map(d => [d.date, d]));

  const cells: Array<StatusHistoryDay | null> = [];
  for (let i = HISTORY_DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    cells.push(byDate.get(d.toISOString().slice(0, 10)) ?? null);
  }
  return cells;
}

/**
 * Worst-of-all-members per calendar day across a set of service keys. Only the fallback for an API
 * that does not send per-card series (`groupSeries`): it knows nothing about member roles, so on such
 * an API an auxiliary service can still colour the whole card's day.
 */
export function combineHistory(
  memberServiceKeys: string[],
  historyByServiceKey: Map<string, StatusHistoryDay[]>,
  today: Date = new Date(),
): Array<StatusHistoryDay | null> {
  const byDate = new Map<string, StatusHistoryDay>();

  for (const key of memberServiceKeys) {
    for (const day of historyByServiceKey.get(key) ?? []) {
      const existing = byDate.get(day.date);
      if (!existing || SEVERITY[day.worstState] > SEVERITY[existing.worstState]) {
        byDate.set(day.date, day);
      }
    }
  }

  return toCells([...byDate.values()], today);
}

/** A card's cells: the API's own series for it when there is one, otherwise combined from its members. */
export function cardHistory(
  groupKey: string,
  memberServiceKeys: string[],
  groupSeries: StatusHistoryGroupSeries[] | undefined,
  historyByServiceKey: Map<string, StatusHistoryDay[]>,
  today: Date = new Date(),
): Array<StatusHistoryDay | null> {
  const series = groupSeries?.find(g => g.key === groupKey);
  return series ? toCells(series.days, today) : combineHistory(memberServiceKeys, historyByServiceKey, today);
}

/**
 * Trims one bar to the newest `take` days. The footer percentage has to describe the same
 * span the bar draws, otherwise a phone shows 30 cells next to a 90-day figure, so a
 * shortened window averages the days it actually renders (no-data days are skipped, not
 * counted as an outage). At the full window the backend's own number is kept verbatim.
 */
export function cutWindow(target: HistoryWindow & { uptimePercent90d: number | null }, take: number): void {
  target.visibleDays = target.historyDays.slice(-take);
  target.monitoredSince = resolveMonitoredSince(target.visibleDays);

  if (take >= target.historyDays.length) {
    target.windowUptimePercent = target.uptimePercent90d;
    return;
  }

  const known = target.visibleDays.filter((d): d is StatusHistoryDay => d !== null);
  target.windowUptimePercent = known.length === 0
    ? target.uptimePercent90d
    : known.reduce((sum, d) => sum + d.uptimePercent, 0) / known.length;
}

/**
 * The date a late-starting series begins, or null when the bar is covered from the start
 * (or has no data at all: an all-empty bar is an unmonitored component, and claiming it
 * has been "monitored since" some date would be worse than saying nothing).
 */
export function resolveMonitoredSince(visibleDays: Array<StatusHistoryDay | null>): string | null {
  const firstKnown = visibleDays.findIndex(d => d !== null);
  if (firstKnown < LEADING_GAP_TOLERANCE) return null;
  return visibleDays[firstKnown]!.date;
}
